import { ComponentProps, useEffect, useMemo, useState } from "react"
import { useCmdkContext } from "../context"
import { useCmdkQuery } from "./useCmdkQuery"
import { useCmdkPage } from "./useCmdkPage"
import { CmdkItem } from "../types"
import fuzzysort from "fuzzysort"

export function useCmdkControl() {
  const registry = useRegistry()
  const ctx = useCmdkContext()
  const inputValue = useCmdkQuery()
  const page = useCmdkPage()

  const query = useMemo(
    () => inputValue.replace(/\s+/g, " ").trim().toLowerCase(),
    [inputValue],
  )

  // Only the open page's groups are listed (root groups have no page), so
  // filter by page before paying for fuzzy matching.
  const pageRegistry = useMemo<typeof registry>(
    () =>
      Object.fromEntries(
        Object.entries(registry).filter(
          ([_, group]) => (group.page ?? null) === (page?.key ?? null),
        ),
      ),
    [registry, page],
  )

  const queriedRegistry = useMemo<typeof registry>(() => {
    if (query === "") {
      return pageRegistry
    }

    return Object.fromEntries(
      Object.entries(pageRegistry)
        .map(([key, value]) => {
          // Server-filtered groups (e.g. search-as-you-type results) are
          // already scoped to the query upstream — re-filtering them locally
          // would drop valid matches whose label doesn't fuzzy-match the raw
          // query (e.g. matched on an id or a description field).
          if (value.serverFiltered) {
            return [key, value] as const
          }

          const groupQuery = value.groupName?.toLowerCase() ?? ""

          return [
            key,
            {
              ...value,
              items: value.items.filter((item) => {
                const result = fuzzysort.single(
                  query,
                  `${groupQuery} ${item.label.toLowerCase()} ${item.detail?.toLowerCase() ?? ""}`,
                )
                return (result?.score ?? 0) > 0.5
              }),
            },
          ] as const
        })
        .filter(([_, value]) => value.items.length > 0),
    )
  }, [pageRegistry, query])

  return {
    getInputProps: () =>
      ({
        value: inputValue,
        onChange: (e) => void ctx.setQuery(e.target.value),
      }) satisfies ComponentProps<"input">,
    getQuery: () => query,
    setQuery: ctx.setQuery,
    registry: queriedRegistry,
    page,
    closePage: () => void ctx.setPage(null),
    // Opens the item's sub-page, or runs its action. Returns whether the
    // palette should close.
    triggerItem: (item: CmdkItem): boolean => {
      if (item.page) {
        ctx.setPage({
          key: item.page.key,
          title: item.label,
          placeholder: item.page.placeholder,
        })
        return false
      }

      item.action?.(item)
      return true
    },
  }
}

export type CmdkControl = ReturnType<typeof useCmdkControl>

function useRegistry() {
  const ctx = useCmdkContext()
  const [registry, setRegistry] = useState(ctx.registeryRef.current)

  useEffect(
    () =>
      ctx.onRegistryUpdated(() => {
        setRegistry({ ...ctx.registeryRef.current })
      }),
    [ctx],
  )

  return registry
}
